import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, File, FileCode, Film, X } from 'lucide-react';
import { ArtifactKindIcon } from '@/entities/artifact';
import type { ProjectContextItem } from '@/features/chats/stores';
import { FileInputWithStatus, FileStatus } from '../../model/useFileAttachments';
import { AttachmentChip, AttachmentImageChip, ProjectContextFileChip } from '../AttachmentChip';
import { Spinner } from '@monksflow/monks-ui';

// Large square for an all-image strip (gallery feel); chip-height (matches the
// AttachmentChip `sm` h-10) otherwise, so a mixed strip stays one uniform row.
const LARGE_THUMBNAIL_SIZE = 80;
const SMALL_THUMBNAIL_SIZE = 40;

// Shared entrance/exit so every chip — file or project context — animates
// identically, since they all live under the one AnimatePresence below.
const CHIP_MOTION = {
  initial: { opacity: 0, scale: 0.8, y: 10 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.8, y: -10 },
  transition: { duration: 0.2, ease: 'easeOut' as const },
};

// eslint-disable-next-line i18next/no-literal-string -- MIME type fragments
const CODE_MIME_PARTS = ['html', 'javascript', 'typescript', 'json', 'xml', 'css', 'text/plain'];

function FileTypeIcon(props: { mimeType: string }) {
  if (props.mimeType.includes('video')) return <Film className="size-4 text-foreground" />;
  if (CODE_MIME_PARTS.some((part) => props.mimeType.includes(part))) {
    return <FileCode className="size-4 text-foreground" />;
  }
  return <File className="size-4 text-foreground" />;
}

function fileLeading(file: FileInputWithStatus) {
  if (file.status === FileStatus.UPLOADING) {
    return <Spinner className="text-foreground-subtle" />;
  }
  if (file.status === FileStatus.FAILED) {
    return <AlertCircle className="size-4 text-destructive" />;
  }
  return <FileTypeIcon mimeType={file.mimeType} />;
}

function RemoveButton(props: { ariaLabel: string; testId: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={props.ariaLabel}
      data-testid={props.testId}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        props.onClick();
      }}
      className="absolute -top-2 -right-2 flex items-center justify-center size-4 rounded-full border border-border-light bg-background shadow-sm opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100"
    >
      <X className="size-2.5" />
    </button>
  );
}

// One ordered attachment, regardless of source: an uploaded file or a selected
// project-context item. The composer renders this single array so files and
// project context read as one strip.
type ComposerAttachment =
  | { type: 'file'; key: string; order: number; file: FileInputWithStatus }
  | { type: 'context'; key: string; order: number; item: ProjectContextItem };

function isImageAttachment(attachment: ComposerAttachment): boolean {
  return attachment.type === 'file'
    ? attachment.file.mimeType.includes('image')
    : attachment.item.kind === 'image';
}

interface AttachmentChipItemProps {
  attachment: ComposerAttachment;
  thumbnailSize: number;
  onRemoveFile: (assetId: string) => void;
  onRemoveProjectContextItem: (id: string) => void;
}

// Module-scope: used inside `attachments.map`, closes over only the 3
// values threaded as props.
function AttachmentChipItem({
  attachment,
  thumbnailSize,
  onRemoveFile,
  onRemoveProjectContextItem,
}: AttachmentChipItemProps) {
  const { t } = useTranslation('chats');

  if (attachment.type === 'file') {
    const { file } = attachment;
    const filename = file.metadata.fileName || file.assetId;
    const remove = (
      <RemoveButton
        ariaLabel={t('chats.composerAttachments.remove', { name: filename })}
        testId="remove-file-button"
        onClick={() => onRemoveFile(file.assetId)}
      />
    );
    return file.mimeType.includes('image') ? (
      <AttachmentImageChip
        directUrl={file.url}
        filename={filename}
        sizePx={thumbnailSize}
        fallback={fileLeading(file)}
        trailing={remove}
      />
    ) : (
      <AttachmentChip
        name={filename}
        leading={fileLeading(file)}
        onClick={
          file.url ? () => window.open(file.url, '_blank', 'noopener,noreferrer') : undefined
        }
        trailing={remove}
      />
    );
  }

  const { item } = attachment;
  const remove = (
    <RemoveButton
      ariaLabel={t('chats.composerAttachments.remove', { name: item.name })}
      testId="remove-project-context-button"
      onClick={() => onRemoveProjectContextItem(item.id)}
    />
  );
  if (item.kind === 'image') {
    return (
      <AttachmentImageChip
        artifactId={item.id}
        directUrl={item.thumbnailUrl}
        filename={item.name}
        sizePx={thumbnailSize}
        fallback={<ArtifactKindIcon kind={item.kind} />}
        trailing={remove}
      />
    );
  }
  // File-backed artifacts (e.g. PDFs) open in a new tab, like attached files.
  if (item.kind === 'file') {
    return (
      <ProjectContextFileChip
        artifactId={item.id}
        name={item.name}
        leading={<ArtifactKindIcon kind={item.kind} />}
        trailing={remove}
      />
    );
  }
  return (
    <AttachmentChip
      name={item.name}
      leading={<ArtifactKindIcon kind={item.kind} />}
      trailing={remove}
    />
  );
}

export interface ComposerAttachmentsProps {
  files: FileInputWithStatus[];
  onRemoveFile: (assetId: string) => void;
  projectContextItems: ProjectContextItem[];
  onRemoveProjectContextItem: (id: string) => void;
}

/**
 * The single attachments strip for the composer. Uploaded files and attached
 * project context are merged into ONE ordered array and rendered through one
 * map under one AnimatePresence, so size, spacing, and add/remove animation are
 * identical across every chip. Image files and image artifacts render as
 * thumbnails; everything else is an AttachmentChip. Thumbnails only grow to the
 * large gallery size when every attachment is an image — a mixed strip keeps
 * them at chip height so the whole strip is one uniform row.
 */
export function ComposerAttachments(props: ComposerAttachmentsProps) {
  // One array in insertion order across both sources: a later add always
  // appends after earlier attachments, regardless of file vs project context.
  // Ties (e.g. unstamped fixtures) keep their source order via a stable sort.
  const attachments: ComposerAttachment[] = [
    ...props.files.map((file) => ({
      type: 'file' as const,
      key: `file-${file.assetId}`,
      order: file.addedAt ?? 0,
      file,
    })),
    ...props.projectContextItems.map((item) => ({
      type: 'context' as const,
      key: `ctx-${item.id}`,
      order: item.addedAt ?? 0,
      item,
    })),
  ].sort((a, b) => a.order - b.order);

  if (attachments.length === 0) return null;

  const allImages = attachments.every(isImageAttachment);
  const thumbnailSize = allImages ? LARGE_THUMBNAIL_SIZE : SMALL_THUMBNAIL_SIZE;

  return (
    <div className="flex w-full flex-row flex-wrap gap-2 px-3 py-3">
      <AnimatePresence mode="popLayout">
        {attachments.map((attachment) => (
          <motion.div key={attachment.key} layout {...CHIP_MOTION}>
            <AttachmentChipItem
              attachment={attachment}
              thumbnailSize={thumbnailSize}
              onRemoveFile={props.onRemoveFile}
              onRemoveProjectContextItem={props.onRemoveProjectContextItem}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
